import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { loadSettings } from "@features/settings/api/settings";
import { useAuth } from "@app/providers/auth";
import { getTenantId } from "@shared/lib/auth";
import { listStores } from "@shared/lib/api/stores";
import { adminHttp } from "@shared/lib/http";
import {
  getPublicCollectionUrl as _getPublicCollectionUrl,
  getPublicProductUrl as _getPublicProductUrl,
  getPublicStorefrontUrl as _getPublicStorefrontUrl,
  getStorefrontHomeUrl as _getStorefrontHomeUrl,
  getStorefrontPreviewUrl as _getStorefrontPreviewUrl,
  getStorefrontProductsUrl as _getStorefrontProductsUrl,
  getStorefrontRootUrl as _getStorefrontRootUrl,
  getStorefrontUrl as _getStorefrontUrl,
  isFullyQualifiedHttpUrl as _isFullyQualifiedHttpUrl,
  openStorefront as _openStorefront,
  resolveStorefrontOpenTarget as _resolveStorefrontOpenTarget,
  type ResolvedStorefrontTarget,
  type StorefrontUrlOptions,
} from "@shared/lib/storefront";
import { useTranslation } from "react-i18next";

export type StorefrontOpenState = {
  canOpen: boolean;
  url: string | null;
  blockers: string[];
};

export type StorefrontOpenResult = {
  opened: boolean;
  url: string | null;
  title: string;
  message: string;
  blockers: string[];
};

type ProductPreviewTarget = {
  id: string;
  slug?: string | null;
  publicUrl?: string | null;
  status?: string | null;
  isActive?: boolean | null;
};

function hashQueryScope(value: string | null | undefined = "") {
  if (!value) return "none";

  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(31, hash) + (value.codePointAt(index) ?? 0);
    // eslint-disable-next-line unicorn/prefer-math-trunc
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
}

function toProductPreviewPath(
  productId?: string | null,
  lang?: string | null,
): string | null {
  const normalizedProductId = productId?.trim();
  if (!normalizedProductId) return null;
  const normalizedLang = lang?.split("-")[0].toLowerCase();
  const prefix = normalizedLang ? `/${normalizedLang}` : "/en";
  return `${prefix}/preview/products/${encodeURIComponent(normalizedProductId)}`;
}

export function useStorefront() {
  const { t: tUi } = useTranslation("ui");
  const storefrontUrlMissingMessage = tUi(
    "completeStoreOnboardingWithAValidStoreSlug",
    {
      defaultValue:
        "Complete store onboarding with a valid store slug; the slug creates the default storefront URL.",
    },
  );
  const auth = useAuth();
  const token = auth.getToken();
  const hasRecoverableSession = Boolean(token || auth.refreshToken);
  const tenantId = getTenantId() ?? auth.tenantId;
  const sessionQueryScope = React.useMemo(() => {
    if (!hasRecoverableSession) return "signed-out";

    return [
      tenantId || "current",
      auth.user?.id ?? "user",
      hashQueryScope(token || auth.refreshToken),
    ].join(":");
  }, [
    auth.refreshToken,
    auth.user?.id,
    hasRecoverableSession,
    tenantId,
    token,
  ]);

  const [storeSlug, setStoreSlug] = React.useState<string | null>(null);
  const [storeDomain, setStoreDomain] = React.useState<string | null>(null);
  const [storePlanId, setStorePlanId] = React.useState<string | null>(null);
  const [storeStatus, setStoreStatus] = React.useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = React.useState<
    string | null
  >(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;

    if (!hasRecoverableSession) {
      setLoading(false);
      setStoreSlug(null);
      setStoreDomain(null);
      setStorePlanId(null);
      setStoreStatus(null);
      setSubscriptionStatus(null);
      return () => {
        alive = false;
      };
    }

    setLoading(true);
    setStoreSlug(null);
    setStoreDomain(null);
    setStorePlanId(null);
    setStoreStatus(null);
    setSubscriptionStatus(null);

    listStores()
      .then((stores) => {
        if (!alive) return;

        const primary =
          stores.find((store) => tenantId && store.tenantId === tenantId) ||
          stores.find((store) => store.isPrimary) ||
          stores[0];

        setStoreSlug(primary?.slug ?? null);
        setStoreDomain(primary?.domain || null);
        setStorePlanId(primary?.planId || null);
        setStoreStatus(primary?.status || null);
        setSubscriptionStatus(primary?.subscriptionStatus || null);
      })
      .catch(() => {
        if (!alive) return;
        setStoreSlug(null);
        setStoreDomain(null);
        setStorePlanId(null);
        setStoreStatus(null);
        setSubscriptionStatus(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [hasRecoverableSession, tenantId]);

  const { data: domainsData } = useQuery({
    queryKey: ["admin.domains", sessionQueryScope],
    queryFn: async () => {
      const res = await adminHttp.get("/api/admin/domains");
      return Array.isArray(res) ? res : [];
    },
    enabled: hasRecoverableSession,
  });

  const effectiveStoreDomain = React.useMemo(() => {
    if (!domainsData) return storeDomain;
    const verified = domainsData.filter((domain: any) => domain.isVerified);
    return (
      verified.find((domain: any) => domain.isPrimary)?.domain ||
      verified[0]?.domain ||
      storeDomain
    );
  }, [domainsData, storeDomain]);

  const { data: settingsData } = useQuery({
    queryKey: ["admin.settings", sessionQueryScope],
    queryFn: () => loadSettings(),
    enabled: hasRecoverableSession && !!storeSlug,
    staleTime: 0,
  });

  const storefrontLocale = settingsData?.localization?.defaultLanguage || "en";
  const storeName = settingsData?.general?.storeName || null;
  const publicStorefrontStore = React.useMemo(
    () => ({
      slug: storeSlug,
      domain: effectiveStoreDomain,
      activeDomain: effectiveStoreDomain,
      storefrontDomain: effectiveStoreDomain,
      locale: storefrontLocale,
    }),
    [effectiveStoreDomain, storeSlug, storefrontLocale],
  );

  const getStorefrontUrl = React.useCallback(
    (options: Omit<StorefrontUrlOptions, "slug" | "domain"> = {}) => {
      return _getStorefrontUrl({
        ...options,
        slug: storeSlug,
        domain: effectiveStoreDomain,
        locale: options.locale ?? storefrontLocale,
        planId: storePlanId,
        storeStatus,
        subscriptionStatus,
      });
    },
    [
      effectiveStoreDomain,
      storePlanId,
      storeSlug,
      storeStatus,
      storefrontLocale,
      subscriptionStatus,
    ],
  );

  const openStorefront = React.useCallback(
    (options: Omit<StorefrontUrlOptions, "slug" | "domain"> = {}) => {
      return _openStorefront({
        ...options,
        slug: storeSlug,
        domain: effectiveStoreDomain,
        locale: options.locale ?? storefrontLocale,
        planId: storePlanId,
        storeStatus,
        subscriptionStatus,
      });
    },
    [
      effectiveStoreDomain,
      storePlanId,
      storeSlug,
      storeStatus,
      storefrontLocale,
      subscriptionStatus,
    ],
  );

  const getOpenStoreTarget = React.useCallback(
    (
      options: Omit<StorefrontUrlOptions, "slug" | "domain"> = {},
    ): ResolvedStorefrontTarget => {
      return _resolveStorefrontOpenTarget({
        ...options,
        slug: storeSlug,
        domain: effectiveStoreDomain,
        locale: options.locale ?? storefrontLocale,
        planId: storePlanId,
        storeStatus,
        subscriptionStatus,
      });
    },
    [
      effectiveStoreDomain,
      storePlanId,
      storeSlug,
      storeStatus,
      storefrontLocale,
      subscriptionStatus,
    ],
  );

  const storefrontTarget = React.useMemo(
    () =>
      _resolveStorefrontOpenTarget({
        slug: storeSlug,
        domain: effectiveStoreDomain,
        locale: storefrontLocale,
        planId: storePlanId,
        storeStatus,
        subscriptionStatus,
      }),
    [
      effectiveStoreDomain,
      storePlanId,
      storeSlug,
      storeStatus,
      storefrontLocale,
      subscriptionStatus,
    ],
  );

  const storefrontRootUrl = React.useMemo(
    () =>
      _getStorefrontRootUrl({
        slug: storeSlug,
        domain: effectiveStoreDomain,
      }),
    [effectiveStoreDomain, storeSlug],
  );

  const storefrontOpen = React.useMemo<StorefrontOpenState>(
    () => ({
      canOpen:
        storefrontTarget.kind === "storefront" &&
        _isFullyQualifiedHttpUrl(storefrontTarget.url),
      url:
        storefrontTarget.kind === "storefront" && storefrontTarget.url
          ? storefrontTarget.url
          : null,
      blockers:
        storefrontTarget.kind === "storefront" && storefrontTarget.url
          ? []
          : [storefrontUrlMissingMessage],
    }),
    [storefrontTarget, storefrontUrlMissingMessage],
  );

  const getProductUrl = React.useCallback(
    (product: ProductPreviewTarget) =>
      _getPublicProductUrl(publicStorefrontStore, product),
    [publicStorefrontStore],
  );

  const getCollectionUrl = React.useCallback(
    (collection: { slug?: string | null; publicUrl?: string | null }) =>
      _getPublicCollectionUrl(publicStorefrontStore, collection),
    [publicStorefrontStore],
  );

  const getProductPreviewUrl = React.useCallback(
    (product: ProductPreviewTarget, previewToken?: string | null) => {
      if (!previewToken) return getProductUrl(product);

      const previewPath = toProductPreviewPath(product.id, storefrontLocale);
      if (!previewPath) return null;

      const previewUrl = getStorefrontUrl({ path: previewPath });
      if (!previewUrl) return null;

      const previewUrlObject = new URL(previewUrl);
      const redirectPath = `${previewUrlObject.pathname}${previewUrlObject.search}${previewUrlObject.hash}`;

      return _getStorefrontPreviewUrl({
        origin: previewUrlObject.origin,
        previewToken,
        locale: storefrontLocale,
        redirectPath,
      });
    },
    [getProductUrl, getStorefrontUrl, storefrontLocale],
  );

  const openStorefrontSafely =
    React.useCallback(async (): Promise<StorefrontOpenResult> => {
      const url =
        storefrontTarget.kind === "storefront" && storefrontTarget.url
          ? storefrontTarget.url
          : null;

      if (!url || !_isFullyQualifiedHttpUrl(url)) {
        return {
          opened: false,
          url: null,
          title: tUi("storefrontCannotBeOpened", {
            defaultValue: "Storefront cannot be opened",
          }),
          message: storefrontUrlMissingMessage,
          blockers: [storefrontUrlMissingMessage],
        };
      }

      window.open(url, "_blank", "noopener,noreferrer");

      return {
        opened: true,
        url,
        title: tUi("storefrontOpened", { defaultValue: "Storefront opened" }),
        message: tUi("storefrontOpenedInANewTab", {
          defaultValue: "Storefront opened in a new tab.",
        }),
        blockers: [],
      };
    }, [storefrontTarget, storefrontUrlMissingMessage, tUi]);

  return {
    storeName,
    storeSlug,
    storeDomain: effectiveStoreDomain,
    loading,
    storefrontOpen,
    getStorefrontUrl,
    getStorefrontHomeUrl: () =>
      _getStorefrontHomeUrl(publicStorefrontStore, storefrontLocale),
    getStorefrontRootUrl: () => storefrontRootUrl,
    getStorefrontProductsUrl: () =>
      _getStorefrontProductsUrl(publicStorefrontStore, storefrontLocale),
    getStorefrontProductUrl: getProductUrl,
    openStorefront,
    openStorefrontSafely,
    getOpenStoreTarget,
    storefrontTarget,
    storefrontRootUrl,
    getPublicStorefrontUrl: () =>
      _getPublicStorefrontUrl(publicStorefrontStore),
    getProductUrl,
    getCollectionUrl,
    getProductPreviewUrl,
  };
}
